import { useEffect, useState } from 'react';
import { useAuth } from '../auth/AuthContext';
import { hasAdFreeExperience } from '../lib/coachAccess';
import { isNativeIOS } from '../lib/platform';
import { PRO_TIER_NAME } from '../lib/productTier';
import { manageAppleSubscriptions } from '../lib/appleIap';
import { checkoutErrText, purchaseSubscriptionPlan, restoreSubscriptionPurchases } from '../lib/subscriptionCheckout';
import type { AuthUser } from '../auth/AuthContext';
import { SubscriptionLegalLinks } from './SubscriptionLegalLinks';
import { AppleOfferCodeField } from './AppleOfferCodeField';
import {
  fallbackSubscriptionOffer,
  loadSubscriptionOffer,
  type SubscriptionOfferDisplay,
} from '../lib/subscriptionOffer';

export function AdFreeUpgradeCard({ user }: { user: AuthUser | null }) {
  const { refresh } = useAuth();
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [offer, setOffer] = useState<SubscriptionOfferDisplay>(() => fallbackSubscriptionOffer('adfree'));
  const [promoFirstMonth, setPromoFirstMonth] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    void loadSubscriptionOffer('adfree').then((o) => {
      if (!cancelled) setOffer(o);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  if (hasAdFreeExperience(user)) return null;

  async function startCheckout() {
    setErr(null);
    setBusy(true);
    try {
      await purchaseSubscriptionPlan('adfree', refresh);
    } catch (e) {
      setErr(checkoutErrText(e));
    } finally {
      setBusy(false);
    }
  }

  async function restore() {
    setErr(null);
    setBusy(true);
    try {
      const ok = await restoreSubscriptionPurchases(refresh);
      if (!ok) setErr('No active subscription found for this Apple ID.');
    } catch (e) {
      setErr(checkoutErrText(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div id="ad-free" className="scroll-mt-24">
      <div className="text-sm font-semibold text-white">{offer.title}</div>
      <p className="mt-1 text-sm text-zinc-400">
        Auto-renewable · {offer.periodLabel}.{' '}
        <strong className="font-medium text-zinc-300">{PRO_TIER_NAME} includes ad-free automatically.</strong>
      </p>

      <div className="mt-3 rounded-2xl border border-white/10 bg-white/[0.04] px-4 py-3">
        <p className="text-[11px] font-medium uppercase tracking-wide text-zinc-500">
          {promoFirstMonth ? 'First month with code' : 'Billed amount'}
        </p>
        <p className="mt-0.5 font-display text-3xl font-bold tabular-nums text-white">
          {promoFirstMonth ?? offer.priceLabel}
        </p>
        <p className="text-sm font-semibold text-zinc-300">
          {promoFirstMonth ? `then ${offer.pricePerPeriodLabel}` : `per ${offer.periodLabel.replace(/^1 /, '')}`}
        </p>
        <p className="mt-2 text-xs text-zinc-500">{offer.services[0]}</p>
      </div>

      {isNativeIOS() ? (
        <p className="mt-3 text-[11px] leading-relaxed text-zinc-500">{offer.renewNote}</p>
      ) : null}

      {isNativeIOS() ? (
        <AppleOfferCodeField
          plan="adfree"
          disabled={busy}
          onApplied={({ firstMonthPriceLabel }) => setPromoFirstMonth(firstMonthPriceLabel)}
          onUnlocked={() => void refresh()}
        />
      ) : null}

      <div className="mt-4 flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-center sm:gap-3">
        <button
          type="button"
          disabled={busy}
          onClick={() => void startCheckout()}
          className="btn-accent-solid btn-press inline-flex w-full items-center justify-center rounded-xl px-5 py-3 text-sm font-semibold shadow-sm transition-[transform,opacity,box-shadow] duration-200 hover:opacity-95 active:opacity-90 disabled:opacity-50 sm:w-auto sm:px-6"
        >
          {busy ? 'Processing…' : `Subscribe · ${offer.pricePerPeriodLabel}`}
        </button>
        {isNativeIOS() ? (
          <>
            <button
              type="button"
              disabled={busy}
              onClick={() => void restore()}
              className="text-xs text-zinc-500 underline-offset-2 hover:text-zinc-300 hover:underline"
            >
              Restore purchases
            </button>
            <button
              type="button"
              disabled={busy}
              onClick={() => void manageAppleSubscriptions()}
              className="text-xs text-zinc-500 underline-offset-2 hover:text-zinc-300 hover:underline"
            >
              Manage subscription
            </button>
          </>
        ) : (
          <span className="text-xs text-zinc-600">
            Or subscribe to {PRO_TIER_NAME} on the Coach page (includes ad-free).
          </span>
        )}
      </div>
      {err ? <p className="mt-3 text-sm text-amber-200">{err}</p> : null}
      <SubscriptionLegalLinks className="mt-4 text-left sm:text-left" />
    </div>
  );
}
